import { Form, Formik } from "formik";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, ThemeProviderV2, Field} from '@blueprint/ui';
import { DatePickerRange } from '@blueprint/datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useModal } from "react-modal-hook";

import { useUserList } from "../../../api/users";
import { Loader } from "../../../common/components/core";
import { SelectField } from "../../../common/components/form";
import { UserActivityLogActionType } from "../UserActivityLogView";

type FilterHandler = {
    show: () => void;
    hide: () => void;
};

export type FilterActivityLogFormValues = {
    actionTakerIds: number[];
    activityType: string;
    dateFrom: Date | null;
    dateTo: Date | null;
};

const useFilterActivityLogHandler = (
    currentFilter: FilterActivityLogFormValues,
    onSubmit: (value: FilterActivityLogFormValues) => void,
): FilterHandler => {
    const { data: userList = [] } = useUserList();

    const [dateRange, setDateRange] = useState<(Date | null)[]>([
        currentFilter.dateFrom,
        currentFilter.dateTo,
    ]);

    const setDates = (date: [Date | null, Date | null]) => {
        setDateRange(date);
    };

    const onResetHandler = () => {
        onSubmit({
            actionTakerIds: [],
            activityType: "",
            dateFrom: null,
            dateTo: null,
        });
        setDates([null, null]);
        hide();
    };


    const [show, hide] = useModal(() => {
        return (
            <ThemeProviderV2>
            <Modal show backdrop centered onHide={hide} id="filter-modal">
                <Formik
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={(value: FilterActivityLogFormValues) => {
                        onSubmit({
                            ...value,
                            dateFrom: dateRange[0],
                            dateTo: dateRange[1],
                        });
                        hide();
                    }}
                    initialValues={{
                        ...currentFilter,
                    }}
                >
                    {({ handleSubmit, isSubmitting }) => (
                        <Loader isLoading={isSubmitting}>
                            <Modal.Header>
                                <h2 className="mb-0">Filter</h2>
                            </Modal.Header>
                            <Modal.Body>
                                <Form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleSubmit}
                                >
                                    <SelectField
                                        name="actionTakerIds"
                                        label="Action Taker"
                                        menuPosition="fixed"
                                        options={
                                            userList
                                                ? userList.map((data) => ({
                                                      value: data.id,
                                                      label: `${data.email}`,
                                                  }))
                                                : []
                                        }
                                    />
                                    <SelectField
                                        name="activityType"
                                        label="Activity"
                                        menuPosition="fixed"
                                        options={Object.values(
                                            UserActivityLogActionType,
                                        ).map((v) => ({
                                            value: formatActionType(v),
                                            label: v,
                                        }))}
                                    />
                                    {/* Timestamp new date picker */}

                                    <Field>
                                        <DatePickerRange
                                            startDate={dateRange[0]}
                                            endDate={dateRange[1]}
                                            nlsTitleDateFrom = {{
                                                title: 'Date from',
                                            }}
                                            nlsTitleDateTo = {{
                                                title: 'Date to',
                                            }}
                                            fromDatePickerProps={{
                                                onChange: (date: Date) => {
                                                     setDates([date, dateRange[1]])
                                                }
                                            }}
                                            toDatePickerProps={{
                                                onChange: (date: Date) => {
                                                    setDates([dateRange[0], date])
                                                }
                                            }}
                                        />
                                    </Field>
                                    <Modal.Footer className="justify-content-between px-0 pb-0 pt-4">
                                        <Button
                                            variant="light.tertiary"
                                            type="reset"
                                            onClick={onResetHandler}
                                        >
                                            Reset
                                        </Button>
                                        <Button variant="light.primary" type="submit">
                                            Apply
                                        </Button>
                                    </Modal.Footer>
                                </Form>
                            </Modal.Body>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [userList, currentFilter, dateRange]);

    return {
        show,
        hide,
    };
};

const formatActionType = (type: string): string => {
    switch (type) {
        case UserActivityLogActionType.CREATED_USER:
            return "Created User";
        case UserActivityLogActionType.UPDATED_USER:
            return "Updated User";
        case UserActivityLogActionType.DELETED_USER:
            return "Deactivated User";
        default:
            return type;
    }
};

export default useFilterActivityLogHandler;
